import React, { useEffect, useState } from "react";
import DateRangePicker from "components/common/DateRangePicker";
import MultiSelect from "components/common/MultiSelect";
import { Icons } from "assets/icons";
import { DASHBOARD_TABS } from "utils/dashboardUtils";
import { useTranslation } from "react-i18next";
import { MONTHS } from "utils/constants";

const FILTERS = ["DAY", "WEEK", "MONTH"];

/**
 * Navigation offset state shape:
 *   offset: number (0 = current period, -1 = one period back, etc.)
 *
 * DAY   → period = calendar month  (Jan, Feb, …)
 * WEEK  → period = calendar week   (Mon–Sun)
 * MONTH → period = calendar year   (2023, 2024, …)
 */

/** Returns { from, to } for the given filter type and offset from "now". */
const getDateRange = (type, offset = 0) => {
  const now = new Date();

  switch (type) {
    case "DAY": {
      // Navigate by month
      const year = now.getFullYear();
      const month = now.getMonth() + offset; // JS handles overflow
      const from = new Date(year, month, 1, 0, 0, 0, 0);
      // If offset === 0, to = today; else to = last day of that month
      const to =
        offset === 0
          ? new Date()
          : new Date(year, month + 1, 0, 23, 59, 59, 999);
      return { from, to };
    }

    case "WEEK": {
      const year = now.getFullYear();
      const month = now.getMonth() + offset;
      const from = new Date(year, month, 1, 0, 0, 0, 0);
      const to =
        offset === 0
          ? new Date()
          : new Date(year, month + 1, 0, 23, 59, 59, 999);
      return { from, to };
    }

    case "MONTH": {
      // Navigate by year
      const year = now.getFullYear() + offset;
      const from = new Date(year, 0, 1, 0, 0, 0, 0);
      // If offset === 0, to = today; else to = Dec 31 of that year
      const to =
        offset === 0 ? new Date() : new Date(year, 11, 31, 23, 59, 59, 999);
      return { from, to };
    }

    default:
      return { from: new Date(), to: new Date() };
  }
};

/** Returns true if the given offset is already the "current" period (can't go forward). */
const isCurrentPeriod = (offset) => offset >= 0;

const CustomerLeadsFilter = ({
  dates,
  setDates,
  filters = [],
  selectedFilters = {},
  setSelectedFilters = () => {},
  activeTabKey,
  setActiveFilter,
  className = "dashboard_filter",
  selectTypeLabel = true,
  selectDateLabel = true,
}) => {
  const [active, setActive] = useState("");
  const [offset, setOffset] = useState(0); // 0 = current period
  const { t } = useTranslation();

  const { refreshDashboard } = Icons || {};

  /** Apply a filter type at the given offset. */
  const applyWithOffset = (type, newOffset) => {
    const { from, to } = getDateRange(type, newOffset);
    setOffset(newOffset);
    setDates([from, to]);
    setActive(type);
    setActiveFilter(type);
  };

  const applyPreset = (type) => {
    setActive(type);
    applyWithOffset(type, 0);
  };

  const handlePrev = () => {
    if (!active) return;
    const newOffset = offset - 1;
    applyWithOffset(active, newOffset);
  };

  const handleNext = () => {
    if (!active || isCurrentPeriod(offset)) return;
    const newOffset = offset + 1;
    applyWithOffset(active, newOffset);
  };

  const handleReset = () => {
    setDates([]);
    applyWithOffset("DAY", 0);
    setOffset(0);
    setSelectedFilters({});
    setActiveFilter("");
  };
  const rightDisabled = !active || isCurrentPeriod(offset);
  const leftDisabled = !active;
  useEffect(() => {
    applyWithOffset("DAY", 0);
  }, []);
  const getPeriodLabel = (type, offset) => {
    const now = new Date();
    if (type === "DAY" || type === "WEEK") {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
    }
    if (type === "MONTH") {
      return String(now.getFullYear() + offset);
    }
    return "";
  };

  return (
    <div className={`${className} g_end gap-2 common_filter`}>
      {/* Preset buttons */}
      <div className="filter_buttons">
        {selectTypeLabel && (
          <label className="dashboard_filter_label">{t("Select Type")}</label>
        )}
        <div className="type_buttons_wrapper">
          {FILTERS.map((item) => (
            <button
              key={item}
              className={`main_btn ${active === item ? "active" : ""}`}
              onClick={() => applyPreset(item)}
            >
              {item.charAt(0) + item.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>
      <div className="d-flex gap-2 align-items-center">
        {/* Reset button */}
        <button
          className="main_btn reset-filter"
          type="button"
          onClick={handleReset}
        >
          <img src={refreshDashboard} width={20} height={20} alt="reset" />
        </button>

        {/* ← Previous period */}
        <button
          className="main_btn nav-arrow"
          type="button"
          onClick={handlePrev}
          disabled={leftDisabled}
          title={t("Previous period")}
          style={{
            opacity: leftDisabled ? 0.4 : 1,
            cursor: leftDisabled ? "not-allowed" : "pointer",
          }}
        >
          &#8592;
        </button>
        {active && (
          <span className="period-label">{getPeriodLabel(active, offset)}</span>
        )}

        {/* → Next period */}
        <button
          className="main_btn nav-arrow"
          type="button"
          onClick={handleNext}
          disabled={rightDisabled}
          title={t("Next period")}
          style={{
            opacity: rightDisabled ? 0.4 : 1,
            cursor: rightDisabled ? "not-allowed" : "pointer",
          }}
        >
          &#8594;
        </button>
      </div>
    </div>
  );
};

export default CustomerLeadsFilter;
